import { ADMIN_API_PREFIX, route } from "./routes.js";
import { z } from "zod";
//#region src/notifications.ts
/**
* `/v1/admin/notifications/apps` — the admin inbox feed and its read cursor.
*
* Producer `AdminNotificationsController` in core-api, whose `admin-notifications.contract.ts`
* holds the wire shapes this file makes runtime-checkable. Both routes carry `channels:read`, the
* ack included: the ack write is owner-keyed RLS on `app.current_user_id`, so a caller can only
* ever write its own cursor row, and requiring `channels:manage` would shut out the members who
* can legitimately read the inbox.
*
* `AdminNotificationKind` is a closed seven-member list. It is declared here and pinned to
* core-api's `ADMIN_NOTIFICATION_KINDS` by that controller's own contract test; a kind added on
* one side alone stops compiling. The per-kind payload shapes stay out of this contract on
* purpose: core-api's contract file documents that two emit sites write different member sets for
* `app.pin-changed`, so the wire member is an open object and the renderer narrows it.
*
* Two facts the shell's `api/client.ts` does not carry: the list route accepts a `since` query
* param, which the shell never sends; and the shell's `ackAdminAppNotifications` is right that
* `cursor` is required, because a bodyless POST is a 400 under the strict pipe.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
*/
/** The feed path. No parameter. */
const ADMIN_APP_NOTIFICATIONS_PATH = `${ADMIN_API_PREFIX}/notifications/apps`;
/** The ack path. No parameter. */
const ADMIN_APP_NOTIFICATIONS_ACK_PATH = `${ADMIN_APP_NOTIFICATIONS_PATH}/ack`;
/**
* `GET /v1/admin/notifications/apps`. The caller's feed across every notification kind, with the
* unread count and the cursor the server resolved. Permission `channels:read`.
*/
const LIST_APP_NOTIFICATIONS_ROUTE = route("GET", ADMIN_APP_NOTIFICATIONS_PATH);
/**
* `POST /v1/admin/notifications/apps/ack`. Stores the caller's read cursor and echoes it back.
* The write is monotonic in the repository (`GREATEST(cursor_at, excluded.cursor_at)`), so this
* route refuses nothing and reports what the caller sent. Permission `channels:read`.
*/
const ACK_APP_NOTIFICATIONS_ROUTE = route("POST", ADMIN_APP_NOTIFICATIONS_ACK_PATH);
/**
* Every kind the feed emits, in the order core-api's `ADMIN_NOTIFICATION_KINDS` declares. The
* pending-onboarding kind is first because it is the one row that asks the reader to act.
*
* Owned by this package. Core-api's `admin-notifications.contract.ts` holds the same seven as a
* `readonly` tuple and its contract test pins the shell mirror both ways; this schema is the
* runtime rendering of that tuple, so WS-6 binds one list rather than two.
*/
const adminNotificationKindSchema = z.enum([
	"app.onboarding.pending",
	"app.installed",
	"app.upgraded",
	"app.permissions.changed",
	"ring.minimum.changed",
	"app.pin-changed",
	"app.permission.invalid"
]);
/**
* One item on the wire.
*
* `id` is stable across re-fetches. Treat it as an opaque string. The base shape is
* `<kind>|<dimension>|<iso>`. Two exceptions add a fourth segment:
* - An item that core-api builds from a `release.app_version_history` row carries its ring as the
*   third segment: `<kind>|<appId>|<ring>|<iso>`. These kinds take this shape:
*   - `app.installed`
*   - `app.upgraded`
*   - `app.permissions.changed`
*   - `app.pin-changed`
*
*   The operator program writes one history row per ring in one transaction, with one
*   `synced_at`. The ring keeps the two items of one version on two rings apart.
* - `app.permission.invalid` adds the audit row id as the fourth segment:
*   `app.permission.invalid|<dimension>|<iso>|<auditRowId>`. Two audit rows can share one
*   millisecond, and the row id keeps their two items apart.
*
* `observedAt` is an ISO timestamp; the controller holds a `Date` and serialises it.
*
* `payload` is an open object and stays open: the two `app.pin-changed` emit sites write
* different member sets, and `app.permission.invalid` spreads an audit `details` bag whose
* members are the emitter's to change. A renderer narrows it per kind.
*/
const adminNotificationSchema = z.object({
	/**
	* Opaque and stable per re-fetch. It has one of three shapes, and this schema's note names the
	* kinds of each:
	* - `<kind>|<dimension>|<iso>`
	* - `<kind>|<appId>|<ring>|<iso>`, for an item built from a history row
	* - `app.permission.invalid|<dimension>|<iso>|<auditRowId>`
	*/
	id: z.string(),
	kind: adminNotificationKindSchema,
	/** ISO-8601. The moment the platform observed the thing this item reports. */
	observedAt: z.string(),
	/** Per-kind members. Open by construction — see this schema's note. */
	payload: z.record(z.string(), z.unknown())
});
/**
* `GET /v1/admin/notifications/apps` query.
*
* `since` is an ISO-8601 instant in the `z.iso.datetime({ offset: true })` spelling (`Z` or a `±HH:MM` offset through
* ±23:59) with a four-digit year of 0001 or later once rendered in UTC. The controller reads the string the caller
* sent, before it parses it, and answers 400 for anything else (`parseWireQueryBound`), so it refuses
* `September 1, 2026` and a bare `2026-09-01`. The 400 body is `{ error: 'invalid_since', code: 'invalid_since', value }`
* plus the four keys `statusCode`, `message`, `timestamp` and `path` that the global exception filter adds.
* The shell sends no query today.
*
* The lower bound is `since`, then the caller's stored cursor. What a feed does with it:
* - The app-version and minimum-ring feeds read from the epoch when neither is present.
* - `app.onboarding.pending` is derived from state and applies no lower bound (R-053).
* - The `app.permission.invalid` feed reads the audit archive. With neither present it reads 30 days back
*   (`AUDIT_INBOX_LOOKBACK_DAYS`, R-031). With either present it reads from that instant, floored at 365 days
*   back (`AUDIT_INBOX_MAX_CURSOR_DAYS`, R-049). Both constants are in core-api's `audit-archive-read.service.ts`.
*/
const listAppNotificationsRequestSchema = z.strictObject({ since: z.string().optional().describe("Read the feed from this ISO-8601 instant: Z or a +-HH:MM offset through +-23:59, a four-digit year of 0001 or later once rendered in UTC, else 400 invalid_since.") });
/**
* `GET /v1/admin/notifications/apps` response.
*
* `unreadCount` counts the items with `observedAt` strictly after the cursor, so a listed
* pending-onboarding item older than the cursor is shown and not counted; the two numbers are
* equal only when nothing listed predates the cursor. `cursorAt` is the newer of the newest
* `observedAt` and the caller's stored cursor.
*
* `cursorAt` is nullable because core-api's declared `AdminNotificationsResponse` is; the handler
* in the tree today always sends a string.
*
* `truncated` tells the reader that the history read hit its cap. core-api reads at most 200
* `release.app_version_history` rows per load (`APP_VERSION_HISTORY_FEED_CAP`), newest first,
* before it maps them to items. `true` means the read returned exactly 200 rows. Then the table
* can hold older history rows past the cursor that this response omits. A read of exactly 200
* rows also gives `true`, whether an older row exists or not. `false` means the read returned
* fewer than 200 rows. Only this flag tells the reader this: one item can stand for several
* history rows, and other kinds add items. A core-api older than this field sends no key, and the
* schema admits that response.
*/
const listAppNotificationsResponseSchema = z.object({
	unreadCount: z.number().int().nonnegative(),
	cursorAt: z.string().nullable(),
	notifications: z.array(adminNotificationSchema),
	/** `true` when the history read returned its cap of 200 rows; absent from an older core-api. */
	truncated: z.boolean().optional()
});
/**
* `POST /v1/admin/notifications/apps/ack` body.
*
* `cursor` is required and is an ISO-8601 instant: the `observedAt` of the newest item the
* caller has displayed. The controller also answers 400 unless the string is in the
* `z.iso.datetime({ offset: true })` spelling (`Z` or a `±HH:MM` offset through ±23:59) and the
* instant has a four-digit year of 0001 or later once rendered in UTC (`parseWireQueryBound`). So
* the bound here is shape and the parse is policy. The 400 body is
* `{ error: 'invalid_cursor', code: 'invalid_cursor', value }` plus the four keys `statusCode`,
* `message`, `timestamp` and `path` that the global exception filter adds.
*/
const ackAppNotificationsRequestSchema = z.strictObject({ cursor: z.string().describe("The observedAt of the newest item the caller displayed, an ISO-8601 instant: Z or a +-HH:MM offset through +-23:59, a four-digit year of 0001 or later once rendered in UTC, else 400 invalid_cursor.") });
/**
* `POST /v1/admin/notifications/apps/ack` response: the server's echo of what it stored, which is
* the only way a caller can tell an accepted ack from one the monotonic write floored.
*/
const ackAppNotificationsResponseSchema = z.object({ ackedAt: z.string() });
/** Every route this file declares, for the package's route census. */
const notificationsRoutes = Object.freeze([LIST_APP_NOTIFICATIONS_ROUTE, ACK_APP_NOTIFICATIONS_ROUTE]);
//#endregion
export { ACK_APP_NOTIFICATIONS_ROUTE, ADMIN_APP_NOTIFICATIONS_ACK_PATH, ADMIN_APP_NOTIFICATIONS_PATH, LIST_APP_NOTIFICATIONS_ROUTE, ackAppNotificationsRequestSchema, ackAppNotificationsResponseSchema, adminNotificationKindSchema, adminNotificationSchema, listAppNotificationsRequestSchema, listAppNotificationsResponseSchema, notificationsRoutes };

//# sourceMappingURL=notifications.js.map